<template>
  <div class="pricing-hero-v2" :class="[$config.project]">
    <div class="container">
      <h1 class="h2">
        365 Data Science Pricing Plans
      </h1>
      <div class="row messages">
        <PlanMessages class="col col-9" version="2" />
      </div>
      <div class="cards">
        <div class="row">
          <div class="col-3 col annual card-wrapper">
            <PlanCard
              :discount="promotion.discount || 0"
              :version="version"
              :plan="{ ...$store.getters['subscriptions/annual'], billText: 'Full access,<br>billed annually' }"
              :stats="stats"
              :plan-specs="planSpecsCards"
              :campaign="new Date(promotion.lastEndDate) > new Date() ? $store.state.campaign : ''"
              :additional-class="planCardAdditionalClass"
            />
          </div>
          <div v-if="$config.project === 'site'" class="col-3 col free card-wrapper">
            <PlanCard
              :version="version"
              :plan="{
                shortTitle: 'Free',
                slug: 'free',
                price: 0,
                free: true,
                billText: 'Limited access,<br>no credit card required'
              }"
              :stats="stats"
              :plan-specs="planSpecsCards"
              :additional-class="{ v2: true }"
              :campaign="new Date(promotion.lastEndDate) > new Date() ? $store.state.campaign : ''"
              @subscribe="$router.push('/signup')"
            />
          </div>
          <div class="col-3 col monthly card-wrapper">
            <PlanCard
              :version="version"
              :plan="{ ...$store.getters['subscriptions/monthly'], billText: 'Most features, billed monthly' }"
              :stats="stats"
              :plan-specs="planSpecsCards"
              :additional-class="{ v2: true }"
            />
          </div>
          <div v-if="$config.project === 'lms'" class="col-3 col quarterly card-wrapper">
            <PlanCard
              :version="version"
              :plan="{ ...$store.getters['subscriptions/quarterly'], billText: 'Billed quarterly' }"
              :stats="stats"
              :plan-specs="planSpecsCards"
              :additional-class="{ v2: true }"
            />
          </div>
          <div v-if="$config.project === 'site'" class="col-3 col business card-wrapper">
            <PlanCard
              class="business"
              :version="version"
              :plan="{ ...$store.getters['subscriptions/business'], billText: 'Business' }"
              :stats="stats"
              :plan-specs="planSpecsCards"
              :additional-class="{ v2: true }"
            />
          </div>
        </div>
      </div>
      <div class="footer">
        <IconPricingBadge />
        <Badges class="mobile-only" />
        <Badges text-mode class="desktop-only" />
        <IconPricingBadge />
      </div>
      <div class="scroll-button">
        <a
          href="/pricing/#compare-features" class="scroll-down" data-event="compare_features_v1.click"
          data-direct-send
          @click.prevent="scrollToFeatures"
        >
          Compare All Features
          <IconPricingArrowDown />
        </a>
      </div>
      <Companies
        v-if="companies && companies.length"
        :companies="companies" title-on-top no-fill
        title="Learn with instructors from top-tier companies:"
      />
    </div>
  </div>
</template>

<script>
import PlanCard from './PlanCardNew';
import Badges from './Badges';
import Companies from '../Campaigns/Companies';
import PlanMessages from './PlanMessages';
import IconPricingBadge from '../assets/svg/icon-pricing-badge.svg?vue-component';
import IconPricingArrowDown from '../assets/svg/icon-pricing-arrow-down.svg?vue-component';

export default {
  components: {
    PlanCard,
    Badges,
    Companies,
    PlanMessages,
    IconPricingBadge,
    IconPricingArrowDown,
  },
  props: {
    version: { type: String, default: '2' },
    stats: { type: Object, default: () => ({}) },
    planSpecsCards: { type: Array, default: () => ([]) },
  },
  data() {
    return {
      promotion: {},
      companies: [
        { icon: 'meta' },
        { icon: 'g' },
        { icon: 'spotify' },
        { icon: 'netflix' },
        { icon: 'ikea' },
        { icon: 'coca-cola' },
        { icon: 'bbc' },
        { icon: 'pwc' },
        { icon: 'morgan-stanley' },
        { icon: 'hsbc' },
        { icon: 'henkel' },
      ],
    };
  },
  computed: {
    planCardAdditionalClass() {
      const classes = { v2: true };

      classes.extended = this.promotion.extend;

      return classes;
    },
  },
  methods: {
    scrollToFeatures() {
      const features = document.getElementById('compare-features');

      if (features) {
        features.scrollIntoView({ behavior: 'smooth' });
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@include bp($bp-desktop) {
  .mobile-only {
    display: none;
  }
}

.pricing-hero-v2 {
  position: relative;
  background-color: var(--main-dark);
  padding: 160px 0 40px;

  &.lms {
    padding-top: 80px;

    h1 {
      font-size: 38px;
    }

    .cards .row {
      justify-content: center;
    }
  }

  .messages {
    justify-content: center;

    ::v-deep .subtext {
      max-width: 100%;
    }
  }

  &:after {
    content: '';
    display: block;
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 0;
    background: linear-gradient(135deg, rgba(86, 191, 199, 0.2) 0.94%, rgba(86, 191, 199, 0) 100%), #000C1F;
  }

  .container {
    position: relative;
    z-index: 1;
  }

  .h2 {
    text-align: center;
    color: var(--main-light);
    margin: 0;
  }

  .cards {
    padding: 64px 0 18px;

    .free,
    .monthly {
      order: 0;
    }

    .annual {
      order: 1;
    }

    .business {
      order: 2;
    }
  }

  .footer {
    display: flex;
    justify-content: center;
    align-items: center;
    padding-top: var(--spacing-36);
    color: var(--main-light-shade-3);

    .badge-wrap {
      margin-top: 0;
      gap: 0;

      ::v-deep .price-lock {
        margin-left: 8px;

        &:before {
          content: '';
          display: inline-block;
          vertical-align: middle;
          width: 4px;
          height: 4px;
          border-radius: 50%;
          background-color: var(--main-dark-light-3);
          margin-right: 4px;
        }

        .tooltip-holder {
          position: static;
          display: inline-block;
          vertical-align: middle;

          .tooltip {
            width: 300px;
          }
        }
      }
    }

    svg:first-child {
      margin-right: var(--spacing-12);
    }

    svg:last-child {
      margin-left: var(--spacing-12);
    }
  }

  .scroll-button {
    text-align: center;
    margin-top: var(--spacing-36);
    font-size: 18px;
    line-height: 18px;
    font-weight: 600;

    a {
      display: inline-block;
      color: var(--accent);
    }

    svg {
      vertical-align: middle;
      margin-left: var(--spacing-4);
    }
  }

  .companies {
    margin-top: var(--spacing-32);

    ::v-deep {
      .title {
        color: var(--main-light-shade-4);
      }

      .trusted-companies {
        padding-top: var(--spacing-32);

        .logos .logo svg {
          height: 24px;
          color: var(--main-light-shade-3);

          [fill="#545C69"],
          [fill="#fff"] {
            fill: var(--main-light-shade-3);
          }
        }
      }
    }
  }
}

@include bp($bp-laptop) {
  .pricing-hero-v2 .companies ::v-deep .trusted-companies .logos .logo svg {
    height: 20px;
  }
}

@include bp($bp-mobile) {
  .desktop-only {
    display: none;
  }

  .pricing-hero-v2 {
    padding-top: 144px;
    height: auto;

    .h2 {
      font-size: 32px;
    }

    &.lms {
      padding-top: 76px;

      h1 {
        font-size: 32px;
      }
    }

    .companies {
      padding-left: 0;
      padding-right: 0;

      ::v-deep .trusted-companies .logos {
        column-gap: 8px;
        row-gap: 22px;

        .logo svg {
          height: 16px;
        }
      }
    }

    .cards {
      .row > .col + .col {
        margin-top: 20px;
      }

      .free {
        order: 3;
      }

      .monthly {
        order: 1;
      }

      .annual {
        order: 0;
      }

      .business {
        order: 4;
      }
    }

    .footer {
      > svg {
        display: none;
      }

      .badge-wrap ::v-deep .price-lock {
        &:before {
          content: none;
        }

        .tooltip-holder {
          position: absolute;

          .tooltip {
            transform: none;
            left: auto;
            right: -40px;
          }
        }
      }
    }

    .scroll-button {
      display: none;
    }
  }
}
</style>
